/**
 * Column-def builder for the table tile's TanStack Table body (S4) — column
 * visibility from `tile.columns`, null rendering (`∅`, muted), and
 * conditional-format cell tinting evaluated per-cell via `thresholdEval`.
 * Pure column shape + cell rendering only — sort/resize model lives in
 * `useReactTable` inside `TableTileView`, kept separate so both stay
 * independently readable/testable.
 *
 * Numeric columns (UX review finding #1) right-align + `tabular-nums` their
 * cells so digit columns scan/compare cleanly instead of ragged-left text.
 * Detection reuses the SAME `analyzeColumns` the chart/stat tiles already use
 * for axis candidates (declared `dataType` first, sampled-value fallback when
 * absent) — one shared cross-engine numeric-detection policy, not a second
 * bespoke heuristic. `numericColumnIds` is also returned so `TableTileView`
 * can right-align/justify-end the matching header cell.
 */
import type { ColumnDef } from '@tanstack/react-table';
import { analyzeColumns } from '../../../charts/utils/columnTypeDetection';
import type { QueryColumn } from '../../../../../types/queryExecution';
import type { TableTile } from '../../types/dashboardTypes';
import { thresholdEval, type ThresholdSeverity } from '../../utils/thresholdEval';

export type TableTileRow = Record<string, unknown>;

export type TableTileColumnsResult = {
  columnDefs: ColumnDef<TableTileRow>[];
  /** Column ids (`col.id ?? col.name`) detected as numeric — header alignment. */
  numericColumnIds: Set<string>;
};

const SEVERITY_CELL_CLASS: Record<ThresholdSeverity, string> = {
  none: '',
  warning: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  critical: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '∅';
  try {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  } catch {
    return '⚠';
  }
}

export function buildTableTileColumns(
  columns: QueryColumn[],
  rows: TableTileRow[],
  tile: TableTile,
): TableTileColumnsResult {
  const visible = tile.columns?.length ? columns.filter((c) => tile.columns!.includes(c.name)) : columns;
  const formatsByColumn = new Map((tile.conditionalFormats ?? []).map((f) => [f.column, f.rule]));

  // Indexed by position (analyzeColumns preserves `columns` order) so it
  // covers the FULL column set even though only `visible` gets a ColumnDef.
  const typeInfo = analyzeColumns(columns, rows);
  const numericColumnIds = new Set(
    columns.filter((_, i) => typeInfo[i]?.category === 'numeric').map((c) => c.id ?? c.name),
  );

  const columnDefs = visible.map((col) => {
    const key = col.id ?? col.name;
    const rule = formatsByColumn.get(col.name);
    const isNumeric = numericColumnIds.has(key);
    const columnDef: ColumnDef<TableTileRow> = {
      id: key,
      header: col.name,
      accessorFn: (row) => row[key],
      // 140 keeps two default-width columns inside a 4-of-12-col tile at
      // common window widths — 160 overflowed by ~20px and forced a
      // permanent horizontal scrollbar (S24 F4).
      size: 140,
      minSize: 60,
      cell: ({ getValue }) => {
        const value = getValue();
        const severity: ThresholdSeverity =
          rule && typeof value === 'number' && Number.isFinite(value) ? thresholdEval(value, [rule]) : 'none';
        const isNullish = value === null || value === undefined;
        return (
          <span
            className={`block truncate ${isNumeric ? 'text-right tabular-nums' : ''} ${isNullish ? 'italic text-muted-foreground' : ''} ${SEVERITY_CELL_CLASS[severity]}`}
            data-testid="table-tile-cell"
            data-severity={severity}
            title={typeof value === 'string' ? value : undefined}
          >
            {formatCellValue(value)}
          </span>
        );
      },
    };
    return columnDef;
  });

  return { columnDefs, numericColumnIds };
}
